/**
 * manage-early: the two reads the booking page waits on, started as soon as the page opens (CC 0315, 0337).
 *
 * manage-main calls startManageEarly() before it loads the app itself (ManagePage, the confirmation
 * canvas, the text catalogue), so the session exchange and the authored page's read are in flight
 * while those scripts download. Deliberately small: the typed client and the URL segment, nothing
 * that renders.
 *
 * Each started request is taken ONCE, by the first consumer for the same segment: useManageSession's
 * first exchange and DesignedConfirmationPage's first read. Every later exchange (recover, retry) and
 * every later read (a new revision) is a fresh request. Nothing here decides anything: a refused
 * exchange still shows the dead state, and the page read's answer is dropped unread unless the
 * exchange's answer lets the page mount.
 */
import { getPublicBookingConfirmationPage, manageSurface } from "@v0y4ge/api-client";
import { readSegment } from "./manage-session";

type Started<T> = { segment: string; promise: Promise<T> } | null;
type ExchangeAnswer = Awaited<ReturnType<typeof manageSurface.createManageSession>>;

let exchange: Started<ExchangeAnswer> = null;
let pageRead: Started<unknown> = null;

/** Start the exchange and the authored page's read for the link this page opened on. */
export function startManageEarly(): void {
  const segment = readSegment();
  if (!segment) return;
  if (!exchange || exchange.segment !== segment) {
    const promise = manageSurface.createManageSession({ token: segment });
    promise.catch(() => { /* handled by the hook that takes it; unhandled if never taken */ });
    exchange = { segment, promise };
  }
  if (!pageRead || pageRead.segment !== segment) {
    const promise = getPublicBookingConfirmationPage(segment) as Promise<unknown>;
    promise.catch(() => { /* handled by the mount that takes it */ });
    pageRead = { segment, promise };
  }
}

/** The started exchange for this segment, once; null when none was started or it was taken. */
export function takeEarlyExchange(segment: string): Promise<ExchangeAnswer> | null {
  if (!exchange || exchange.segment !== segment) return null;
  const p = exchange.promise; exchange = null; return p;
}

/** The started page read for this booking, once. */
export function takeEarlyPageRead(segment: string): Promise<unknown> | null {
  if (!pageRead || pageRead.segment !== segment) return null;
  const p = pageRead.promise; pageRead = null; return p;
}
